import { Drop, Fire, Gauge, Lightning } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Card, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type Props = {
	total: number | undefined;
	water: number | undefined;
	heat: number | undefined;
	electricity?: number | undefined;
	loading: boolean;
	to?: string;
};

export function MetersBreakdownCard({
	total,
	water,
	heat,
	electricity,
	loading,
	to,
}: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<Card
			className={cn(
				"bg-muted/30 transition-colors",
				to && "cursor-pointer hover:ring-foreground/25",
			)}
			onClick={to ? () => navigate(to) : undefined}
		>
			<CardHeader className="flex flex-col gap-2">
				<div className="flex items-center gap-2">
					<div className="rounded-lg bg-slate-100 p-1.5 text-slate-600 dark:bg-slate-900/50 dark:text-slate-400">
						<Gauge className="size-5" aria-hidden="true" />
					</div>
					<span className="text-muted-foreground text-xs">
						{t("dashboard.cards.meters")}
					</span>
					{loading ? (
						<Skeleton className="ml-auto h-5 w-16" aria-label="Ładowanie…" />
					) : (
						<span className="text-foreground ml-auto text-base font-semibold tabular-nums">
							{total ?? "—"}{" "}
							<span className="text-muted-foreground text-xs font-normal">
								{t("dashboard.cards.metersUnit")}
							</span>
						</span>
					)}
				</div>
				<div className="text-muted-foreground flex flex-col gap-1 text-xs">
					<span className="flex items-center justify-between gap-2">
						<span className="inline-flex items-center gap-1.5">
							<Drop
								className="size-4 text-blue-600 dark:text-blue-400"
								aria-hidden="true"
							/>
							{t("dashboard.cards.meterWater")}
						</span>
						<span className="tabular-nums">{loading ? "…" : (water ?? 0)}</span>
					</span>
					<span className="flex items-center justify-between gap-2">
						<span className="inline-flex items-center gap-1.5">
							<Fire
								className="size-4 text-orange-600 dark:text-orange-400"
								aria-hidden="true"
							/>
							{t("dashboard.cards.meterHeat")}
						</span>
						<span className="tabular-nums">{loading ? "…" : (heat ?? 0)}</span>
					</span>
					{electricity != null && (
						<span className="flex items-center justify-between gap-2">
							<span className="inline-flex items-center gap-1.5">
								<Lightning
									className="size-4 text-green-600 dark:text-green-400"
									aria-hidden="true"
								/>
								{t("dashboard.cards.meterElectricity")}
							</span>
							<span className="tabular-nums">
								{loading ? "…" : electricity}
							</span>
						</span>
					)}
				</div>
			</CardHeader>
		</Card>
	);
}
